import type {ApiResponseData} from '@frontend/types/AppTypes'
import {handleSuccessResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {ApiRequestService} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import type {
    ChatDetailsData,
    ChatMessageData,
    ChatsFilter,
} from './ChatsModuleTypes'

// API чатов пользователя.
export const ChatsApi = {

    // Получить список чатов.
    async getChatsList(
        filter: ChatsFilter,
        limit: number,
        offset: number = 0
    ): Promise<ChatsListResponseData> {
        return ApiRequestService
            .get<ChatsListResponseData>('/v1/cabinet/chats', {
                filter,
                limit: String(limit),
                offset: String(offset),
            })
            .then(response => response.data)
    },

    // Получить данные чата.
    async getChatDetails(id: number): Promise<ChatsDetailsResponseData> {
        return ApiRequestService
            .get<ChatsDetailsResponseData>(`/v1/cabinet/chat/${id}`)
            .then(response => response.data)
    },

    // Получить часть сообщений чата (созданных до beforeId).
    async getChatMessages(
        chatId: number,
        limit: number,
        beforeId: number | null = null
    ): Promise<ChatsMessagesListResponseData> {
        return ApiRequestService
            .get<ChatsMessagesListResponseData>(`/v1/cabinet/chat/${chatId}/messages`, {
                limit: String(limit),
                beforeId: beforeId ? String(beforeId) : undefined,
            })
            .then(response => response.data)
    },

    // Добавить текстовое сообщение в чат.
    // Нужно обязательно передать adId/executorId или chatId.
    // Если chatId не передается, то будет создан новый чат.
    async addMessageToChat(
        message: string,
        chatId: number | null = null,
        adId: number | null = null,
        executorId: number | null = null
    ): Promise<ChatMessageData> {
        return ApiRequestService
            .post<AddChatMessagesResponseData>(`/v1/cabinet/chat/${chatId}/message`, {
                chatId,
                adId,
                executorId,
                message,
            })
            .then(response => response.data.message)
    },

    // Добавить файл с опциональным текстовым сообщением в чат.
    // Нужно обязательно передать adId или chatId.
    // Если chatId не передается, то будет создан новый чат.
    async addFileToChat(
        file: File,
        message: string,
        chatId: number | null,
        adId: number | null = null,
        executorId: number | null = null
    ): Promise<ChatMessageData> {
        const formData: FormData = ApiRequestService.formDataFromObject({
            chatId,
            adId,
            executorId,
            message,
        })
        if (file) {
            formData.append('file', file, file.name)
        }
        return ApiRequestService
            .post<AddChatMessagesResponseData>(`/v1/cabinet/chat/${chatId}/message`, formData)
            .then(response => response.data.message)
    },

    // Пометить чужие сообщения в чате прочитанными.
    async readSomeMessagesInChat(chatId: number, messagesIds: number[]): Promise<ApiResponseData> {
        return ApiRequestService
            .put<ApiResponseData>(`/v1/cabinet/chat/${chatId}/read`, {
                ids: messagesIds,
            })
            .then(response => {
                handleSuccessResponse(response)
                return response.data
            })
    },

    // Пометить все чужие сообщения в указанных чатах прочитанными.
    async readAllMessagesInChats(chatsIds: number[]): Promise<ApiResponseData> {
        return ApiRequestService
            .put<ApiResponseData>('/v1/cabinet/chats/read', {
                ids: chatsIds,
            })
            .then(response => {
                handleSuccessResponse(response)
                return response.data
            })
    },

    // Удалить указанные чаты.
    async deleteChats(chatsIds: number[]): Promise<ApiResponseData> {
        return ApiRequestService
            .delete<ApiResponseData>('/v1/cabinet/chats', {
                ids: chatsIds,
            })
            .then(response => {
                handleSuccessResponse(response)
                return response.data
            })
    },
}

export interface ChatsListResponseData extends ApiResponseData {
    records: ChatDetailsData[]
    count: number
}

interface ChatsDetailsResponseData extends ApiResponseData {
    chat: ChatDetailsData
    canSendImages: boolean
}

export interface ChatsMessagesListResponseData extends ApiResponseData {
    records: ChatMessageData[]
    count: number
}

interface AddChatMessagesResponseData extends ApiResponseData {
    message: ChatMessageData
}
